import * as _ from 'underscore';
import { ListProperty } from './listProperty';
import { Event } from './event';
import { EditObject } from './editObject';
import { Util } from './util';
import { DataSource } from './data';
import { ListOptions, SortOptions } from '../interfaces/options';
import {
	SelectionMode,
	EditableType,
	KeyCode,
	PropertyType,
	SortState,
	DataChangedEventType
} from '../enums';
import { IEvent } from '../interfaces';
import { DataChangedEvent } from '../events';
import { ConfirmModal } from './modal';
import { Icons, IconSet } from '../const';
import { DefaultListOptions } from '../const/defaultOptions';
import { Localization } from '../compile';

export class List<T> {
	public dataSource: DataSource<T>;
	public idProperty: string;
	protected options: ListOptions<T>;
	public view: KnockoutComputed<T[]>;
	protected properties: ListProperty[];
	private clickOnProperty: (property: ListProperty) => void;
	private sortSettingStack: {
		[key: string]: SortOptions;
	};
	public isEmpty: KnockoutComputed<boolean>;
	public viewIsEmpty: KnockoutComputed<boolean>;
	public itemsCountInfo: KnockoutComputed<string>;
	protected confirmModal: ConfirmModal;
	// selection
	private _selectedItems: KnockoutObservableArray<T>;
	public get selectedItems(): T[] {
		return this._selectedItems();
	}
	public isSelected: (item: T) => boolean;
	public toggleSelection: (item: T) => boolean;
	public selectionMode: KnockoutObservable<SelectionMode>;
	private isSelectionOn: KnockoutComputed<boolean>;
	private isMultipleSelectionOn: KnockoutComputed<boolean>;
	public isAllSelected: KnockoutComputed<boolean>;
	public toggleAllSelection: () => boolean;
	private deleteLabel: KnockoutComputed<string>;
	public selectionCountLabel: KnockoutComputed<string>;
	public selectionDeleteCountLabel: KnockoutComputed<string>;
	private _onSelectionToggled: Event<T[]>;
	public get onSelectionToggled(): IEvent<T[]> {
		return this._onSelectionToggled;
	}
	public selectionCount: KnockoutComputed<number>;
	private selectionCss: KnockoutComputed<string>;
	private hasIsSelectableFn: boolean;
	private isSelectableFn: (item: T) => boolean;
	private _notSelectableItems: KnockoutObservableArray<T>;
	private isSelectable: (item: T) => boolean;
	private notSelectableItemsCount: KnockoutComputed<number>;
	//paging
	public isPagingOn: boolean;
	private currentPage: KnockoutObservable<number>;
	private pageCount: KnockoutComputed<number>;
	private isPreviousPageable: KnockoutComputed<boolean>;
	private isNextPageable: KnockoutComputed<boolean>;
	private clickPreviousPage: () => void;
	private clickNextPage: () => void;
	public clickFirstPage: () => void;
	private clickLastPage: () => void;
	private clickPage: (page: number) => void;
	private setPage: (d: any, e: any, page: number) => void;
	private pagingIndexKeyUp: KnockoutObservable<number>;
	private deferredDeleteCountLabel = '';
	private pagerInputFocus: KnockoutObservable<boolean>;
	private pageSetterVisible: KnockoutComputed<boolean>;
	public viewSize: KnockoutComputed<number>;
	// inline editing
	protected editable: boolean;
	protected editableType: EditableType;
	private _editingItems: KnockoutObservableArray<T>;
	private editObjects: Map<any, EditObject>;
	public get editingItems(): T[] {
		return this._editingItems();
	}
	public isEditing: (item: T) => boolean;
	public toggleEditing: (item: T, sync?: boolean) => boolean;
	public getEditObject: (item: T) => EditObject;
	public editingCount: KnockoutComputed<number>;
	public editingCountLabel: KnockoutComputed<string>;
	constructor(options?: ListOptions<T>) {
		this.options = $.extend(
			true,
			{},
			DefaultListOptions,
			<ListOptions<T>>{
				dataSource: {
					filter: {
						bypass: (item: T) => {
							return this._selectedItems().indexOf(item) >= 0;
						}
					}
				}
			},
			options || {}
		);
		if (this.options.dataSource instanceof DataSource) {
			this.dataSource = this.options.dataSource;
		} else {
			this.dataSource = new DataSource(this.options.dataSource);
		}

		this.isEmpty = ko.pureComputed(() => this.dataSource.length() <= 0);
		this.viewIsEmpty = ko.pureComputed(() => !this.isEmpty() && this.view().length <= 0);
		this.itemsCountInfo = ko.pureComputed(() => {
			return Localization.Items.concat(': ', this.dataSource.length().toString());
		});

		this._onSelectionToggled = new Event<T[]>();

		this.idProperty = this.options.idProperty;
		this.properties = this.options.properties
			? this.options.properties.map(property => new this.options.propertyModel(property))
			: [];

		this.view = this.dataSource.view;

		this.sortSettingStack = {};

		this.clickOnProperty = (property: ListProperty) => {
			this.updateSortSetting(property.nextSortState());
		};

		this.confirmModal = new ConfirmModal();

		//#region inline editing
		this.editableType = this.options.editable;
		this.editable = _.some(this.properties, (property: ListProperty) => property.isEditable);
		this._editingItems = ko.observableArray<T>();
		this.editObjects = new Map<any, EditObject>();

		this.isEditing = (item: T) => {
			return this._editingItems.indexOf(item) >= 0;
		};

		this.toggleEditing = (item: T, sync: boolean = false) => {
			if (
				(this.editable && this.editableType == EditableType.ALL) ||
				(this.editableType == EditableType.ONLYNEW && this.dataSource.isNew(item))
			) {
				if (this._editingItems.indexOf(item) >= 0) {
					if (sync) {
						// Todo sync to server
						var editObject = this.editObjects.get(item);
						if (editObject && editObject.validate()) {
							editObject.sync();
							this.dataSource.sync(item).then(
								() => {
									this.removeEditingObject(item);
								},
								() => {
									editObject.validate(true);
									editObject.cancel();
								}
							);
							return true;
						}
					} else {
						this.cancelEditing(item);
						return false;
					}
				} else {
					// set before push
					this.editObjects.set(item, new EditObject(item, this.properties));
					this._editingItems.push(item);
					return true;
				}
			}
			return false;
		};

		this.dataSource.onDataChanged.subscribe((eventData: DataChangedEvent<T>) => {
			if (eventData.eventType == DataChangedEventType.ADDED) {
				this.toggleEditing(eventData.data[0]);
			}
		});

		this.getEditObject = (item: T) => {
			return this.editObjects.get(item);
		};

		this.editingCount = ko.pureComputed(() => this._editingItems().length);
		this.editingCountLabel = ko.pureComputed<string>(() => {
			var extension = '';
			if (this.isAllSelected()) {
				extension = Localization.All;
			} else {
				var length: number;
				extension =
					(length = this.selectionCount()) > 0 || (length = this.editingCount()) > 0
						? `(${length})`
						: '';
			}
			return extension;
		});

		//#endregion

		//#region selection
		this.selectionMode = ko.observable(this.options.selectionMode);
		this.isSelectionOn = ko.pureComputed(() => this.selectionMode() != SelectionMode.NONE);
		this.isMultipleSelectionOn = ko.pureComputed(
			() => this.selectionMode() == SelectionMode.MULTIPLE
		);

		// not selectable items
		this.hasIsSelectableFn =
			!_.isUndefined(this.options.isSelectable) && _.isFunction(this.options.isSelectable);
		this.isSelectableFn = this.options.isSelectable;
		this._notSelectableItems = ko.observableArray<T>();
		this.notSelectableItemsCount = ko.pureComputed(
			() => (this.hasIsSelectableFn ? this._notSelectableItems().length : 0)
		);

		if (this.hasIsSelectableFn) {
			this.dataSource.onDataChanged.subscribe((data: DataChangedEvent<T>) => {
				if (this.hasIsSelectableFn) {
					var notSelectableItems = Util.cloneArray(data.data).filter(
						(item: T) => !this.isSelectableFn(item)
					);
					this._notSelectableItems(notSelectableItems);
				}
			});
		}

		this.isSelectable = (item: T) => {
			if (this.hasIsSelectableFn) {
				return this._notSelectableItems.indexOf(item) < 0;
			}
			return true;
		};

		this._selectedItems = ko.observableArray<T>();
		this.selectionCount = ko.pureComputed(() => this._selectedItems().length);
		this.isSelected = (item: T) => {
			return this._selectedItems.indexOf(item) >= 0;
		};
		this.toggleSelection = (item: T) => {
			var selectionMode = this.selectionMode();
			if (
				selectionMode != SelectionMode.NONE &&
				(!this.hasIsSelectableFn || this.isSelectable(item))
			) {
				if (this._selectedItems.indexOf(item) >= 0) {
					if (selectionMode != SelectionMode.KEEPLAST || this._selectedItems().length > 1) {
						this._selectedItems.remove(item);
						this._onSelectionToggled.notify([item]);
					}
					return false;
				} else {
					if (selectionMode == SelectionMode.SINGLE || selectionMode == SelectionMode.KEEPLAST) {
						this._selectedItems.removeAll();
					}
					this._selectedItems.push(item);
					this._onSelectionToggled.notify([item]);
					return true;
				}
			}
			return false;
		};
		this.isAllSelected = ko.computed({
			read: () => {
				var dataLength = this.dataSource.length();

				var newValue =
					this.isSelectionOn() &&
					this.isMultipleSelectionOn() &&
					dataLength > 0 &&
					dataLength == this.selectionCount() + this.notSelectableItemsCount();
				return newValue;
			},
			write: (selected: boolean) => {
				if (this.isSelectionOn()) {
					if (selected && this.isMultipleSelectionOn()) {
						var clonedData = Util.cloneArray<T>(this.dataSource.data);
						if (this.hasIsSelectableFn) {
							clonedData = clonedData.filter((item: T) => this.isSelectable(item));
						}
						this._selectedItems(clonedData.concat(Util.cloneArray<T>(this.dataSource.newData)));
					} else {
						this._selectedItems.removeAll();
					}
					this._onSelectionToggled.notify(this._selectedItems());
				}
			}
		});

		this.toggleAllSelection = () => {
			if (this.selectionCount() > 0) {
				this.isAllSelected(false);
				return false;
			} else if (this.dataSource.isFilterOn()) {
				var filteredData = this.dataSource.filteringDataMap.map();
				if (this.hasIsSelectableFn) {
					filteredData = filteredData.filter((item: T) => this.isSelectable(item));
				}
				this.selectItems(filteredData);
				return false;
			} else {
				var isAllSelected = !this.isAllSelected();
				this.isAllSelected(isAllSelected);
				return isAllSelected;
			}
		};

		this.selectionCountLabel = ko.pureComputed<string>(() => {
			var extension = '';
			if (this.isAllSelected()) {
				extension = Localization.All;
			} else {
				var length: number;
				extension = (length = this.selectionCount()) > 0 ? `(${length})` : '';
			}
			return extension;
		});

		this.selectionDeleteCountLabel = ko.pureComputed<string>(() => {
			if (this.selectionCount() > 0) {
				this.deferredDeleteCountLabel = this.selectionCountLabel();
			}

			return this.deferredDeleteCountLabel;
		});

		this.selectionCss = ko.pureComputed<string>(() => {
			if (this.isAllSelected()) {
				return Icons.Ok;
			} else if (this.selectionCount() > 0 && this.selectionCount() == this.viewSize()) {
				return IconSet.SomeChildSelected;
			}

			return '';
		});

		this.deleteLabel = ko.pureComputed<string>(() => {
			return Localization.Delete.concat(' ', this.selectionCountLabel());
		});

		//#endregion

		//#region paging
		this.isPagingOn = this.dataSource.isPagingOn;
		this.pagerInputFocus = ko.observable<boolean>(false);
		this.currentPage = ko.pureComputed(() => this.dataSource.pagingDataMap.currentPage());
		this.pagingIndexKeyUp = ko.observable<number>(this.currentPage());
		this.pageCount = ko.pureComputed(() => this.dataSource.pagingDataMap.pageCount());
		this.isPreviousPageable = ko.pureComputed(() =>
			this.dataSource.pagingDataMap.isPreviousPageable()
		);
		this.isNextPageable = ko.pureComputed(() => this.dataSource.pagingDataMap.isNextPageable());
		this.pageSetterVisible = ko.pureComputed(() => this.pageCount() > 10);

		this.clickFirstPage = () => {
			this.dataSource.firstPage();
		};

		this.clickPreviousPage = () => {
			this.dataSource.previousPage();
		};

		this.clickNextPage = () => {
			this.dataSource.nextPage();
		};

		this.clickLastPage = () => {
			this.dataSource.lastPage();
		};

		this.clickPage = (page: number) => {
			this.dataSource.page(page);
		};

		this.currentPage.subscribe(newValue => {
			this.pagingIndexKeyUp(newValue);
		});

		this.pagerInputFocus.subscribe(focus => {
			if (!focus) {
				this.setPageIndexString();
			}
		});

		this.setPage = (d: any, e: JQueryKeyEventObject) => {
			if (e.keyCode == KeyCode.ENTER) {
				this.setPageIndexString();
			}
		};

		this.viewSize = ko.pureComputed<number>(() => {
			return this.dataSource.filteringDataMap.map().length;
		});
		//#endregion
	}

	private setPageIndexString() {
		let pageIndex = this.pagingIndexKeyUp();
		if ($.isNumeric(pageIndex)) {
			this.clickPage(Util.convert(pageIndex, PropertyType.NUMBER) as number);
		}
	}

	public selectAll(): void {
		this.isAllSelected(true);
	}

	public reload(...params: any[]): Promise<DataSource<T>> {
		// TODO: block while loading
		// TODO: remember previous selections, UseCase: select 3 - delete 1 -> 2 should stay selected
		this.cancel(true);
		return this.dataSource.reload(...params);
	}

	private updateSortSetting(sortSetting: SortOptions) {
		if (sortSetting.state == SortState.NONE) {
			delete this.sortSettingStack[sortSetting.key];
		} else {
			this.sortSettingStack[sortSetting.key] = sortSetting;
		}
		this.dataSource.sort(_.values(this.sortSettingStack));
	}

	public getItemBy(value: any, property: string = this.idProperty) {
		return this.dataSource.getItemBy(value, property);
	}

	public getItemById(id: any): T {
		return this.getItemBy(id);
	}

	public selectItem(item: T | string, property: string = this.idProperty): T {
		let itemValue: string = _.isString(item) ? (item as string) : (item as T)[property];
		return this.selectBy(itemValue, property);
	}

	public selectBy(value: any, property: string = this.idProperty): T {
		let selectItem: T = this.getItemBy(value, property);
		if (selectItem) {
			if (this.toggleSelection(selectItem)) {
				return selectItem;
			}
		}
		return undefined;
	}

	public selectItems(items: T[] | string[], property: string = this.idProperty): void {
		if (_.isArray(items) && items.length > 0) {
			let selectionMode = this.selectionMode();
			if (selectionMode == SelectionMode.SINGLE) {
				this.selectItem(items[0], property);
			} else if (selectionMode == SelectionMode.MULTIPLE) {
				let itemValues: string[] = _.isString(items[0])
					? (items as string[])
					: (items as T[]).map(item => item[property] as string);
				const data = this.dataSource.data(),
					length = data.length;

				for (let i = 0; i < length; i++) {
					if (Util.containsRemove(itemValues, data[i][property])) {
						this._selectedItems.push(data[i]);
						// no more to select, break loop
						if (itemValues.length == 0) {
							i = length;
						}
					}
				}
				this._onSelectionToggled.notify(this._selectedItems());
			}
		}
	}

	public save(): void {
		let toSaveItems: T[];
		if (this.selectionCount() > 0) {
			toSaveItems = _.intersection(this.editingItems, this.selectedItems);
		} else {
			toSaveItems = this.editingItems;
		}
		Util.cloneArray(toSaveItems).forEach(saveItem => this.toggleEditing(saveItem, true));
		this.clearSelection();
	}

	public cancel(all: boolean = false): void {
		let toCancelItems: T[];
		if (this.selectionCount() > 0 && !all) {
			toCancelItems = _.intersection(this.editingItems, this.selectedItems);
		} else {
			toCancelItems = this.editingItems;
		}
		Util.cloneArray(toCancelItems).forEach(saveItem => this.cancelEditing(saveItem));
		this.clearSelection();
	}

	public cancelEditing(item: T): void {
		if (this.dataSource.isNew(item)) {
			this.dataSource.delete(item);
		}
		this.removeEditingObject(item);
	}

	private removeEditingObject(item: T): void {
		this._editingItems.remove(item);
		this.editObjects.delete(item);
	}

	public clearSelection(): void {
		this.isAllSelected(false);
	}

	public delete(item: T): Promise<any> {
		if (this.options.confirmBeforeDelete) {
			const deferred = $.Deferred<any>();
			this.confirmModal.open(
				() => {
					this.dataSource.delete(item).then(() => {
						this.clearSelection();
						deferred.resolve();
					}, deferred.reject);
				},
				this,
				() => deferred.reject()
			);
			return deferred.promise();
		} else {
			return this.dataSource.delete(item);
		}
	}

	public deleteSelected(): void {
		let selectionCount: number = this.selectionCount();
		if (this.isAllSelected() || selectionCount > 0) {
			if (this.options.confirmBeforeDelete) {
				if (selectionCount > 1) {
					this.confirmModal.message(Localization.Confirm.Delete.Plural);
				} else {
					this.confirmModal.message(Localization.Confirm.Delete.Singular);
				}
				this.confirmModal.open(this._deleteSelected, this);
			} else {
				this._deleteSelected();
			}
		}
	}

	private _deleteSelected(): void {
		if (this.isAllSelected()) {
			this.dataSource
				.deleteAll()
				.then(() => this.cancel(true), () => {})
				.then(() => this.clearSelection());
		} else {
			this.dataSource
				.deleteItems(this.selectedItems)
				.then(
					() => {
						_.intersection(this.editingItems, this.selectedItems).forEach(toCancel =>
							this.cancelEditing(toCancel)
						);
					},
					() => {}
				)
				.then(() => this.clearSelection());
		}
	}
}
